import type { ProductLoggerRecord } from '@myua/product-logger';
import { createProductLogger } from '@myua/product-logger';
// eslint-disable-next-line import/named
import { TListenerApi } from '@myua/rtk-helper';
import { Selector } from '@reduxjs/toolkit';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { select } from 'redux-saga/effects';
import { z } from 'zod';
import { CurrentUser } from './model';

export function createProductLogger2<ProductLog extends ProductLoggerRecord, TStoreShape>(
  service: string,
  _selector: Selector<TStoreShape, CurrentUser | null | undefined>,
  zodSchema: z.ZodType<ProductLog, any, any>,
) {
  const logger = createProductLogger({ service });

  function log(record: ProductLog) {
    try {
      const safeParsedRecord = zodSchema.safeParse(record);
      if (safeParsedRecord.success) {
        console.info(
          `${safeParsedRecord.data.action}${
            safeParsedRecord.data.sub_action ? ':' + safeParsedRecord.data.sub_action : ''
          }`,
          safeParsedRecord.data.extra,
        );
        logger.log(safeParsedRecord.data);
      } else {
        console.error('Failed to parse log record:', record);
        console.error(safeParsedRecord.error);
      }
    } catch (x) {
      console.error(x);
    }
  }

  function loggerLapi(lapi: Pick<TListenerApi<TStoreShape>, 'getState'>, record: ProductLog) {
    const user = _selector(lapi.getState());
    if (!user) {
      console.warn(
        `[skipped] ${record.action}${record.sub_action ? ':' + record.sub_action : ''}`,
        record.extra,
      );
      return;
    }
    log(record);
  }

  /**
   * @deprecated
   */
  function* loggerSaga(record: ProductLog): any {
    const user: ReturnType<typeof _selector> = yield select(_selector);
    if (!user) {
      console.warn(
        `[skipped] ${record.action}${record.sub_action ? ':' + record.sub_action : ''}`,
        record.extra,
      );
      return;
    }
    log(record);
  }

  function useLogger() {
    const user = useSelector(_selector);
    const loggerInner = useCallback(
      (record: ProductLog) => {
        if (!user) {
          console.warn(
            `[skipped] ${record.action}${record.sub_action ? ':' + record.sub_action : ''}`,
            record.extra,
          );
          return;
        }
        log(record);
      },
      [user],
    );
    return loggerInner;
  }
  return { loggerSaga, useLogger, logger, loggerLapi };
}
